import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useUpsertAssetToCurrentValuationWithEntityUpdatesMutation } from '@/modules/assetValuation/graphql/mutations.generated';
import { makeActivateEntityUpdateInput } from '@/modules/entities/components/ConvertToActiveMenuItem/makeActivateEntityUpdateInput';
import { INSURANCE_POLICY_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { useUpdateEntitiesMutation } from '@/modules/entities/graphql/UpdateEntities.generated';
import {
  InsurancePolicyDetails,
  InsurancePolicyDetailsSubformType,
} from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import {
  buildWithAssetStaticForCreate,
  decodeSitusKind,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { useTrackAssetTaxSitusEvent } from '@/modules/entities/StructuredAssetsSubform/useTrackAssetTaxSitusEvent';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { getEntitySubtypeUpdateFieldForPolicy } from '@/modules/estate/estateHoldings/estateHoldings.utils';
import { Operations as LiabilitiesTableOperations } from '@/modules/liabilities/LiabilitiesTable/graphql/LiabilitiesTable.generated';
import { mapFormDataToInput } from '@/modules/liabilities/LiabilityModal/CreateLiabilityModal.utils';
import { useCreateLiabilityWithEntityUpdatesMutation } from '@/modules/liabilities/LiabilityModal/graphql/LiabilityModal.generated';
import {
  LiabilityFormShape,
  NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
} from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import {
  AssetValueV2OwnershipType,
  AugmentedCreateAssetV2Input,
  AugmentedUpdateEntityInput,
  EntityKind,
  EntityStage,
  InsurancePolicyKind,
} from '@/types/schema';

import {
  EstatePage_EntityFragment,
  Operations,
} from '../../graphql/EstatePageData.generated';
import {
  AssetHoldingsFormShape,
  HoldingType,
  InsuranceHoldingFormShape,
} from './AddHoldingsFormModal.types';

/**
 * Determines if an entity should be disabled based on the selected holding type.
 *
 * Based on business rules:
 * - Assets: Cannot be added to InsurancePersonalAccount entities
 * - Liabilities: Can be added to all entity kinds (no restrictions)
 * - Insurance: Cannot be added to specific personal account and trust types
 */
export function shouldDisableEntityForHoldingType(
  entityKind: EntityKind,
  holdingType: HoldingType
): boolean {
  switch (holdingType) {
    case 'asset':
      // Assets cannot be added to InsurancePersonalAccount
      return (
        entityKind === EntityKind.InsurancePersonalAccount ||
        entityKind === EntityKind.IlitTrust
      );

    case 'liability':
      // Liabilities can be added to all entity kinds - no restrictions
      return false;

    case 'insurance':
      // Insurance cannot be added to these entity kinds
      return [
        EntityKind.IndividualPersonalAccount,
        EntityKind.JointPersonalAccount,
        EntityKind.RetirementPersonalAccount,
        EntityKind.CustodialPersonalAccount,
        EntityKind.QualifiedTuitionPersonalAccount,
        EntityKind.QprtTrust,
        EntityKind.GratTrust,
        EntityKind.SoleProprietorshipBusinessEntity,
      ].includes(entityKind);

    default:
      return false;
  }
}

export interface UseHandleAssetsSubmitParams {
  selectedHoldingsGroup: EstatePage_EntityFragment | undefined;
  householdId: string | null;
  upsertAssetToCurrentValuationWithEntityUpdates: ReturnType<
    typeof useUpsertAssetToCurrentValuationWithEntityUpdatesMutation
  >[0];
  onCompleted: () => void;
  onError: (err: Error) => void;
}

export interface UseHandleInsuranceSubmitParams {
  selectedHoldingsGroup: EstatePage_EntityFragment | undefined;
  householdId: string | null;
  updateEntities: ReturnType<typeof useUpdateEntitiesMutation>[0];
  onCompleted: () => void;
  onError: (err: Error) => void;
}

export interface UseHandleLiabilitySubmitParams {
  entities: EstatePage_EntityFragment[];
  householdId: string | null;
  createLiabilityWithEntityUpdates: ReturnType<
    typeof useCreateLiabilityWithEntityUpdatesMutation
  >[0];
  onCompleted: () => void;
  onError: (err: Error) => void;
}

/**
 * Custom hook that handles asset form submission by creating assets using UpsertAssetToCurrentValuation mutation
 */
export function useHandleAssetsSubmit(params: UseHandleAssetsSubmitParams) {
  const { showFeedback } = useFeedback();
  const trackAssetTaxSitusEvent = useTrackAssetTaxSitusEvent();
  const { assetClassesById } = useTenantDetailsContext();

  const {
    selectedHoldingsGroup,
    householdId,
    upsertAssetToCurrentValuationWithEntityUpdates,
    onCompleted,
    onError,
  } = params;

  return async (data: AssetHoldingsFormShape): Promise<void> => {
    if (!selectedHoldingsGroup || !householdId) {
      return;
    }

    try {
      const assets = data.structuredAssetsSubform.assets || [];

      // Filter out empty assets (assets with no value or title)
      const validAssets = assets.filter(
        (asset) => asset.value && asset.value.gt(0) && asset.title?.trim()
      );

      if (validAssets.length === 0) {
        showFeedback('Please add at least one asset with a title and value.');
        return;
      }

      const entityType = getEntityTypeFromEntityKind(
        selectedHoldingsGroup.kind
      );

      // Create asset inputs for the mutation. AddHoldings only adds new
      // rows, so any user-set situs always rides withAssetStatic.create —
      // there's no path to an in-place AssetStatic update from this modal.
      const assetInputs: AugmentedCreateAssetV2Input[] = validAssets.map(
        (asset) => ({
          create: {
            classID: asset.categoryId,
            displayName: asset.title || 'Asset',
            householdID: householdId,
          },
          withAssetValue: {
            create: {
              ownedValue: asset.value,
              ownershipType: AssetValueV2OwnershipType.ValueBased,
              householdID: householdId,
            },
          },
          withAssetStatic: buildWithAssetStaticForCreate(asset, {
            entityID: selectedHoldingsGroup.id,
            householdID: householdId,
          }),
        })
      );

      // If the entity stage is PreCreated, update it to Active
      // We do this silently without showing the "converted to active" feedback since
      // the user is focused on adding assets, not converting entity stages

      // Build entity update inputs for PreCreated entities
      const entityUpdateInputs =
        selectedHoldingsGroup.stage === EntityStage.PreCreated
          ? [
              makeActivateEntityUpdateInput({
                entityId: selectedHoldingsGroup.id,
                subtypeId: selectedHoldingsGroup.subtype.id,
                entityType: getEntityTypeFromEntityKind(
                  selectedHoldingsGroup.kind
                ),
              }),
            ]
          : [];

      // Upsert assets and update entities in a single mutation
      await upsertAssetToCurrentValuationWithEntityUpdates({
        variables: {
          entityID: selectedHoldingsGroup.id,
          input: assetInputs,
          updateEntities: entityUpdateInputs,
        },
        refetchQueries: [Operations.Query.EstatePageData],
        awaitRefetchQueries: true,
        onCompleted: () => {
          // Skip auto-filled defaults to avoid phantom 'created' events.
          for (const asset of validAssets) {
            if (asset.taxSitus && asset.taxSitus !== asset._originalTaxSitus) {
              trackAssetTaxSitusEvent('created', {
                source: 'add_holdings',
                householdId,
                entityId: selectedHoldingsGroup.id,
                assetClass: assetClassesById[asset.categoryId]?.displayName,
                situsKind: decodeSitusKind(asset.taxSitus),
                entityType,
              });
            }
          }
          onCompleted();
        },
        onError,
      });
    } catch (error) {
      onError(error as Error);
    }
  };
}

/**
 * Custom hook that handles insurance form submission by transforming policy data and calling mutation
 */
export function useHandleInsuranceSubmit(
  params: UseHandleInsuranceSubmitParams
) {
  const {
    selectedHoldingsGroup,
    householdId,
    updateEntities,
    onCompleted,
    onError,
  } = params;

  return async (data: InsuranceHoldingFormShape): Promise<void> => {
    if (!selectedHoldingsGroup || !householdId) {
      return;
    }

    const entityType = getEntityTypeFromEntityKind(selectedHoldingsGroup.kind);

    // Transform insurance policy data to the expected withPolicies format
    const policies: InsurancePolicyDetails[] =
      (data.insurancePolicyDetailsSubform as InsurancePolicyDetailsSubformType)
        ?.policies || [];
    const withPolicies = policies.map((policy) => ({
      create: {
        carrierName: policy.insuranceCarrier || '',
        deathBenefitAmount: policy.deathBenefitAmount?.toString() || '0',
        documentIDs: policy.documentIds || [],
        ...(policy.premiumInitialDueDate && {
          initialPremiumDueDate: policy.premiumInitialDueDate.toISOString(),
        }),
        kind: policy.policyType,
        notes: policy.notes || '',
        policyHolderIDs: policy.policyHolderIDs?.filter(Boolean) || [], // Remove empty strings
        policyNumber: policy.policyNumber || '',
        premiumAmount: policy.premiumAmount?.toString() || '0',
        ...(policy.premiumFrequency && {
          premiumFrequency: policy.premiumFrequency,
        }),
        ...(policy.policyStartDate && {
          startDate: policy.policyStartDate.toISOString(),
        }),
        survivorship:
          policy.policyType !== InsurancePolicyKind.Term
            ? policy.isConvertible || false
            : false,
        ...(policy.cashValue && {
          cashValue: policy.cashValue.toString(),
        }),
        ...(policy.loanBalanceOutstanding && {
          loanBalanceOutstanding: policy.loanBalanceOutstanding.toString(),
        }),
        // Add termDurationYears for term policies
        ...(policy.policyType === InsurancePolicyKind.Term &&
          policy.termLength && {
            termDurationYears: policy.termLength,
          }),
        householdID: householdId,
      },
    }));

    const entitySubtypeUpdateField =
      getEntitySubtypeUpdateFieldForPolicy(entityType);
    if (!entitySubtypeUpdateField) {
      throw new Error(
        `Unsupported entity type for insurance policies: ${entityType}. Supported types: ${INSURANCE_POLICY_ENTITY_TYPES.join(', ')}`
      );
    }

    const updateEntityInput: AugmentedUpdateEntityInput = {
      id: selectedHoldingsGroup.id,
      update:
        selectedHoldingsGroup.stage === EntityStage.PreCreated
          ? {
              stage: EntityStage.Active,
            }
          : {},
      [entitySubtypeUpdateField]: {
        id: selectedHoldingsGroup.subtype.id,
        update: {},
        withPolicies,
      },
    };

    await updateEntities({
      refetchQueries: [Operations.Query.EstatePageData],
      variables: { input: [updateEntityInput] },
      onCompleted,
      onError,
    });
  };
}

/**
 * Custom hook that handles liability form submission by mapping form data and calling mutation
 */
export function useHandleLiabilitySubmit(
  params: UseHandleLiabilitySubmitParams
) {
  const { reportError } = useReportError();

  const {
    entities,
    householdId,
    createLiabilityWithEntityUpdates,
    onCompleted,
    onError,
  } = params;

  return async (data: LiabilityFormShape): Promise<void> => {
    if (!householdId) {
      return;
    }

    try {
      // If the issuer or recipient entities are in PreCreated stage, activate them
      // We do this silently without showing the "converted to active" feedback since
      // the user is focused on adding liabilities, not converting entity stages

      // Collect entities that need to be activated
      const entitiesToActivate = [];

      // Find issuer entity if it exists
      if (
        data.issuerId &&
        data.issuerId !== NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
      ) {
        const issuerEntity = entities.find(
          (entity) => entity.id === data.issuerId
        );
        if (issuerEntity) {
          entitiesToActivate.push(issuerEntity);
        }
      }

      // Find recipient entity if it exists
      if (
        data.recipientId &&
        data.recipientId !== NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
      ) {
        const recipientEntity = entities.find(
          (entity) => entity.id === data.recipientId
        );
        if (recipientEntity) {
          entitiesToActivate.push(recipientEntity);
        }
      }

      // Build entity update inputs for PreCreated entities
      const preCreatedEntities = entitiesToActivate.filter(
        (entity) => entity.stage === EntityStage.PreCreated
      );

      const entityUpdateInputs = preCreatedEntities.map((entity) => {
        const entityType = getEntityTypeFromEntityKind(entity.kind);
        return makeActivateEntityUpdateInput({
          entityId: entity.id,
          subtypeId: entity.subtype.id,
          entityType,
        });
      });

      const liabilityInput = mapFormDataToInput(data, householdId);

      // Create liability and update entities in a single mutation
      await createLiabilityWithEntityUpdates({
        awaitRefetchQueries: true,
        refetchQueries: [
          Operations.Query.EstatePageData,
          LiabilitiesTableOperations.Query.LiabilitiesTable,
        ],
        variables: {
          input: liabilityInput,
          updateEntities: entityUpdateInputs,
        },
        onCompleted,
        onError,
      });
    } catch (error) {
      // Only handle unexpected errors that aren't GraphQL mutation failures
      // GraphQL errors are handled by the onError callback
      reportError('Unexpected error while adding liability', error as Error);
      throw error; // Re-throw to maintain existing error handling behavior
    }
  };
}
